'use client'

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { useAtom } from 'jotai';
import { usePathname, useRouter } from 'next/navigation';
import { useRef, useEffect, useState, memo, useCallback  } from 'react';
import useThrottledCallback from '@/lib/hooks/useThrottledCallback';
import useWindowResize from '@/lib/hooks/useWindowResize';

// lib files
import { canAccessStorage } from '@/lib/helpers/is-storage-available';
import { continuousPlay as continuousPlayAtom } from '@/lib/atoms/continuous-play';
import { FeaturedPreview } from '@/lib/types/api/show-data';
import { getQueryString } from '@/lib/helpers/get-query-string';
import { getUserId as getClientUserId } from '@/lib/profile';
import { INTERNALLY_NAVIGATING_KEY } from '@/lib/constants';
import { sendPlayerPostMessage } from '@/lib/helpers/send-player-postmessage';
import { signinModalAtom, SignInModalStateEnum } from '@/lib/atoms/signin-modal';
import { StationData } from '@/lib/types/api/stations-data';
import { useIdentity } from '@/lib/hooks/useIdentity';
import { useIsUserHoveringOverVideo, useIsPlayerReady, useIsClosedCaptionsSettingsOpen } from '@/lib/hooks';
import { VideoClass, RelatedVideoAsset, VideoTypeEnum } from '@/lib/types/api/video';

// helper functions
import { buildPlayerSrc, PlayerConfig } from './buildPlayerSrc';

// components
import MezzanineVideoInitializer from '@/components/MezzanineVideoInitializer/MezzanineVideoInitializer';
import PassportBenefitScreen from './PassportBenefitScreen';
import UnavailableMessage from './UnavailableMessage';
import VideoPlayerOverlay from './VideoPlayerOverlay';

// styles
import styles from './VideoPlayer.module.scss';

interface PlayerIframeProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  playerSource?: string;
  title: string;
}

/**
 * PlayerIframe
 * A memoized component for our Player iFrame.
 * This was done in order to help prevent needless rerenders
 * of this expensive piece of UI.
 * @param {event} e - click event
 * @returns {boolean}
*/
const PlayerIframe = memo(function PlayerIframe(props: PlayerIframeProps) {
  const { iframeRef, playerSource, title } = props;

  return (
    <iframe
      ref={iframeRef}
      src={playerSource}
      title={ title }
      name="player"
      className={styles.video_player_iframe}
      allow="autoplay; encrypted-media; fullscreen"
      loading="eager"
      data-testid="video-player"
      allowFullScreen
    />
  )
});

interface VideoPlayerProps {
  className?: string;
  csStationDataPassportLearnMoreUrl?: string;
  depStationData?: StationData;
  moveControlsUpBreakpoint?: number;
  onPlaybackEnded?: () => void;
  playerConfig: PlayerConfig;
  showOverlay?: boolean;
  video: VideoClass | RelatedVideoAsset | FeaturedPreview;
}

const VideoPlayer = (props: VideoPlayerProps) => {
  const {
    className,
    csStationDataPassportLearnMoreUrl,
    depStationData,
    moveControlsUpBreakpoint,
    onPlaybackEnded,
    playerConfig,
    showOverlay = false,
    video,
  } = props;
  // cachedIdentity, not identity: a held profile is enough to build from, and
  // waiting for confirmation would leave a returning member without a playable
  // iframe. The src-string guard below keeps a later confirmation from remounting.
  const { cachedIdentity, isSignedIn, isResolving } = useIdentity()
  const [_, setIsOpen] = useAtom(signinModalAtom);
  const [continuousPlay] = useAtom(continuousPlayAtom);
  const [playerSource, setPlayerSource] = useState<string | undefined>(undefined);
  const playerSourceRef = useRef<string | undefined>(undefined);
  const router = useRouter()

  const videoHasFlags = typeof (video as VideoClass).flags !== 'undefined';
  const isPassportVideo = videoHasFlags && (video as VideoClass).flags.is_mvod;
  const isNotPlayable = videoHasFlags && (video as VideoClass).flags.is_playable === false;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pathname = usePathname();

  // Passport and VPPA come from the confirmed profile, so a member whose
  // pbs_uid cookie is unreadable (cross-domain scoping, Safari ITP,
  // post-redirect timing) is no longer treated as a non-member. Both default to
  // the non-blocking answer while there's no confirmed identity.
  const isPassportMember = cachedIdentity?.isPassport === true;
  // Only an explicit `false` counts as declined. An absent flag must not block
  // playback, which is the opposite conservatism to the activation flow's.
  const vppaDeclined = cachedIdentity?.vppaAccepted === false;

  // The pbs_uid cookie is read here as a cheap synchronous hint only, never
  // as the identity we build with. A visitor with neither a uid hint nor a
  // cached profile is almost certainly logged out; their src is `loggedout` and
  // never changes, so build on first mount instead of costing them a render
  // waiting on /sso/profile. Anyone who might have an identity waits for a
  // terminal state, so we never emit a speculative `no` that flips to `yes` and
  // reloads the iframe. Unlike the previous cookie-as-identity approach, a
  // cached profile with an unreadable cookie now waits rather than building a
  // permanently `loggedout` src.
  const [hasUidHint] = useState(() => !!getClientUserId());
  const mightHaveIdentity = hasUidHint || isSignedIn;
  const readyToBuild = !isResolving || !mightHaveIdentity;

  // This useEffect deals with building the player src URL
  useEffect(() => {
    if (!readyToBuild) return;

    const clonedPlayerConfig: PlayerConfig = { ...playerConfig };

    // Applying user's continuous play settings from local storage
    if (!continuousPlay) {
      clonedPlayerConfig.disableContinuousPlay = true;
    }

    if (canAccessStorage('sessionStorage')) {
      // Reading session storage *here* to prevent weird re-renders of the iframe
      if (sessionStorage.getItem(INTERNALLY_NAVIGATING_KEY) === 'true'){
        clonedPlayerConfig.autoplay = true;
      }
    }

    // An unknown is_passport is coerced to false here. resolveProfile carries the
    // last known value forward (RWEB-9736), so a member should not reach this with
    // an unknown value.
    const possibleNewSource = buildPlayerSrc(
      video,
      cachedIdentity?.uid,
      cachedIdentity?.isPassport ?? false,
      clonedPlayerConfig,
    );

    // Only update player source in state if it's changed
    if (possibleNewSource !== playerSourceRef.current) {
      setPlayerSource(possibleNewSource);
      playerSourceRef.current = possibleNewSource;
    }
  },
  [
    continuousPlay,
    cachedIdentity,
    // we create the player src url on pathname change
    // this helps prevent weird race conditions between next.js and the browser
    pathname,
    playerConfig,
    readyToBuild,
    video,
  ])

  const isPlayerReady: boolean = useIsPlayerReady();
  const [isPlaying, setIsPlaying] = useState(false);

  // There are some contexts (e.g. Show Detail Heros) where we want to shift the controls up.
  // We supply the optional `moveControlsUpBreakpoint` prop to indicate that we should do
  // this, as well as what breakpoint above which we should.
  const handleIsAboveBreakpoint = useCallback(() => {
    if (window.matchMedia(`(min-width: ${moveControlsUpBreakpoint}px)`).matches) {
      sendPlayerPostMessage('controlsUp')
    } else {
      sendPlayerPostMessage('controlsDefault')
    }
  }, [moveControlsUpBreakpoint])

  // Do this check on mount
  useEffect(() => {
    if (moveControlsUpBreakpoint && isPlayerReady) {
      handleIsAboveBreakpoint();
    }
  }, [moveControlsUpBreakpoint, isPlayerReady, handleIsAboveBreakpoint])

  const onWindowResize = useWindowResize();

  // check if the viewport is above the desired breakpoint on resize
  onWindowResize(useThrottledCallback(() => {
    if (moveControlsUpBreakpoint) {
      handleIsAboveBreakpoint();
    }
  }));

  // We listen for post-messages coming from the embedded iframe Player
  // to synchronize the overlay visibility with Player controls
  const isUserHoveringOverVideo: boolean = useIsUserHoveringOverVideo();
  const isClosedCaptionsSettingsOpen: boolean = useIsClosedCaptionsSettingsOpen();

  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    const userPressedSpacebar = e.key === " ";
    const userPressedCKey = e.key === "c";
    const userPressedKKey = e.key === "k";
    const userPressedMKey = e.key === "m";
    const userPressedFKey = e.key === "f";
    const userPressedRightArrow = e.key === "ArrowRight";
    const userPressedLeftArrow = e.key === "ArrowLeft";
    const userPressedUpArrow = e.key === "ArrowUp";
    const userPressedDownArrow = e.key === "ArrowDown";

    // grab the element currently in focus
    const focusEl = document.querySelector(':focus');
    // if an element is actually in focus (which won't be the case on a fresh page load)
    // use it's tagName, else use an empty string
    const focusTag = focusEl ? focusEl.tagName : ``;
    // make sure the focused element is not something that should respond to a spacebar normally
    const focusIsNotInputOrButton = focusTag !== `BUTTON` && focusTag !== `INPUT` && focusTag !== `TEXTAREA`;

    const sendKeyboardCommand = (command: string) => {
      e.preventDefault();
      if (iframeRef) {
        iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ command }), '*');
      }
    };

    if (focusIsNotInputOrButton) {
      switch (true) {
        case userPressedSpacebar || userPressedKKey:
          // toggle video player
          sendKeyboardCommand('toggle');
          break;
        case userPressedRightArrow:
          // skip forward 10 seconds
          sendKeyboardCommand('skipForward');
          break;
        case userPressedLeftArrow:
          // skip backward 10 seconds
          sendKeyboardCommand('skipBackward');
          break;
        case userPressedMKey:
          // mute video
          sendKeyboardCommand('mute');
          break;
        case userPressedFKey:
          // toggle fullscreen video
          sendKeyboardCommand('toggleFullscreen');
          break;
        case userPressedCKey:
          // toggle captions
          sendKeyboardCommand('closedCaptions');
          break;
        case userPressedUpArrow:
          // volume up
          sendKeyboardCommand('volumeUp');
          break;
        case userPressedDownArrow:
          // volume down
          sendKeyboardCommand('volumeDown');
          break;
        default:
          break;
      }
    }
}, [])

  // @TODO with the passport benefit screen in dotorg now, is this necessary?
  const handleSignInClick = useCallback((event: MessageEvent) => {
    const isPlayerSignInPostMessage =
      // does the message come from player?
      // adding this as a security check
      event.origin.indexOf(playerConfig.host) !== -1 &&
      // does it indicate that the user hit "sign in"?
      // this is the most brittle point of this feature.
      // if the sign in button stops working in player,
      // it likely changed this message data
      event.data === 'gtm-event:Passport,Sign In';

      if(isPlayerSignInPostMessage) {
        setIsOpen(SignInModalStateEnum.True)
      }
  }, [setIsOpen, playerConfig.host])

  const handleMessage = useCallback((event: MessageEvent) => {
    let messageType;
    let upNextVideoSlug;

    if (typeof event.data === 'string') {
      if(event.data === 'gtm-event:Passport,Sign In') {
        return handleSignInClick(event)
      }

      if(event.data.match(/"command":"navigate-to-continuous-play-video"/)) {
        const messageData = event.data.match(/"payload":{"slug":"([^"]+)"}/)
        messageType = 'upNextVideoSlug'
        upNextVideoSlug = messageData ? messageData[1] : null;
      } else {
        const messageData = event.data.match(/"event":"videojs:([^"]+)"/);
        messageType = messageData ? messageData[1] : null;
      }
    }

    switch (messageType) {
      case 'upNextVideoSlug':
        if (upNextVideoSlug) {
          const queryString = getQueryString();
          const params = new URLSearchParams(queryString)
          if (
            params.get('continuousplayautoplay') &&
            params.get('continuousplayautoplay') === 'true'
          ) {
            router.push(`/video/${upNextVideoSlug}/?continuousplayautoplay=true`)
          } else {
            router.push(`/video/${upNextVideoSlug}`)
          }
        }
        break;
      case 'play':
        setIsPlaying(true)
        break;
      case 'pause':
        setIsPlaying(false)
        break;
      case 'finished':
        setIsPlaying(false)
        onPlaybackEnded?.()
        break;
    }
  }, [handleSignInClick, onPlaybackEnded, router])

  let classNames = `${styles.video_player_container}`;
  if (className) {
    classNames += ` ${className}`
  }

  useEffect(()=>{
    window.addEventListener('message', handleMessage);
    window.addEventListener('keydown', handleKeyPress)

    return ()=>{
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('keydown', handleKeyPress)
    }
  })

  let mezzImgSrc = null;

  // sorry for all of these ts-ingores - we are dealing with a lot of different video types
  // @ts-ignore
  if (video.image) {
    // @ts-ignore
    mezzImgSrc = video.image;
  // @ts-ignore
  } else if (video.images && video.images['asset-mezzanine-16x9']) {
    // @ts-ignore
    mezzImgSrc = video.images['asset-mezzanine-16x9'];
  // @ts-ignore
  } else if (video.mezzanine16x9ImageUrl) {
    // @ts-ignore
    mezzImgSrc = video.mezzanine16x9ImageUrl;
  }

  let content = null;
  switch (true) {
    case videoHasFlags && !isPassportVideo && isNotPlayable:
      content = (
        <>
          {/* Note the mezz image is kept separate from the unavailable messsage purely for CSS z-index reasons */}
          { mezzImgSrc && (
            <MezzanineVideoInitializer
              videoType={ VideoTypeEnum.Video }
              imgSrc={mezzImgSrc}
              alt={video.title}
              showWatchButton={false}
              onClick={() => {}}
              verticalOffsetButton={false}
              width={1440}
              className={styles.video_image}
            />
          )}
          <UnavailableMessage />
        </>
      )
      break;
    case isPassportVideo && isResolving:
      content = null;
      break;
    case isPassportVideo && !isResolving && !isPassportMember:
    // @TODO this will need to be updated when the is_playable flag is updated to not
    // return false for passport users who have not accepted VPPA.
    // But for the time being this prevents a weird situation where the regular video overlay
    // is present but the player displays the passport benefit screen.
    case isPassportVideo && !isResolving && isPassportMember && vppaDeclined:
      content = (
        <>
          { mezzImgSrc && (
            <MezzanineVideoInitializer
              videoType={ VideoTypeEnum.Video }
              imgSrc={mezzImgSrc}
              alt={video.title}
              showWatchButton={false}
              onClick={() => {}}
              verticalOffsetButton={false}
              width={1440}
              className={styles.video_image}
            />
          )}
          <PassportBenefitScreen video={video as VideoClass} depStationData={depStationData} csStationDataPassportLearnMoreUrl={csStationDataPassportLearnMoreUrl} />
        </>
      )
      break;
    case isPassportVideo && !isResolving && isPassportMember:
    default:
      content = (
        <PlayerIframe
          iframeRef={iframeRef}
          playerSource={playerSource}
          title={video.title}
        />
      )
      break;
  }

  return (
    <div className={classNames} data-sentry-component="VideoPlayer" data-sentry-source-file="VideoPlayer.tsx">
      { showOverlay &&
        <VideoPlayerOverlay
          video={video as VideoClass}
          isPlaying={isPlaying}
          isUserHoveringOverVideo={isUserHoveringOverVideo}
          isClosedCaptionsSettingsOpen={isClosedCaptionsSettingsOpen}
        />
      }
      {content}
    </div>
  );
}

export default VideoPlayer;
